import { Component, OnDestroy, OnInit } from '@angular/core';
import { Product } from '@spartacus/core';
import { BehaviorSubject, filter, map, Observable, of, Subscription } from 'rxjs';
import { CustomCarousel } from '../../../../store-front/misc/cms-components/simple-responsive-carousel/carousel.model';
import { CurrentProductService } from '@spartacus/storefront';
import { mediaConfig } from '../../../../store-front/default-media.config';
import { ToolsService } from '../../../../../../core/services/tools.service';
import { DEVICE_SIZE } from '../../../../../../core/models/others.model';
import { SimpleGalleryService } from '../../simple-gallery.service';
import { GALLERY_MEDIA_TYPE, MainContent, YOUTUBE_THUMBNAIL } from '../../core/simple-gallery.model';

@Component({
  selector: 'simple-thumbs',
  templateUrl: './simple-thumbs.component.html',
  styleUrl: './simple-thumbs.component.scss',
  standalone: false,
})
export class SimpleThumbsComponent implements OnInit, OnDestroy {
  GALLERY_MEDIA_TYPE = GALLERY_MEDIA_TYPE;
  mainContent$ = this.simpleGalleryService.getContent();
  mainContent: MainContent | undefined;

  product$ = this.currentProductService.getProduct();
  subscription = new Subscription();

  thumbs$ = this.product$.pipe(
    filter(p => p !== null),
    map((p: Product) => this.createThumbs(p)),
  );

  corrouselConfig$ = new BehaviorSubject<CustomCarousel>({
    value: [],
  });

  responsiveOptions = [
    {
      breakpoint: mediaConfig.mediaFormats!['widescreen'].width?.toString() + 'px',
      numVisible: 4,
      numScroll: 4,
    },
    {
      breakpoint: mediaConfig.mediaFormats!['desktop'].width?.toString() + 'px',
      numVisible: 4,
      numScroll: 4,
    },
    {
      breakpoint: mediaConfig.mediaFormats!['tablet'].width?.toString() + 'px',
      numVisible: 4,
      numScroll: 4,
    },
    {
      breakpoint: mediaConfig.mediaFormats!['mobile'].width?.toString() + 'px',
      numVisible: 3,
      numScroll: 3,
    },
  ];

  constructor(
    private currentProductService: CurrentProductService,
    private toolsService: ToolsService,
    private simpleGalleryService: SimpleGalleryService,
  ) {}

  ngOnInit(): void {
    this.checkCarouselConfig();
    this.checkMainContent();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  checkMainContent() {
    this.subscription.add(
      this.simpleGalleryService.getContent().subscribe(content => {
        this.mainContent = content;
      }),
    );
  }

  checkCarouselConfig() {
    this.subscription.add(
      this.thumbs$.subscribe(thumbs => {
        const currentSize = this.toolsService.getCurrentSize();
        const carouselConfig = this.corrouselConfig$.getValue();
        const showIndicators =
          (currentSize === DEVICE_SIZE.MOBILE && thumbs.length > 3) ||
          (currentSize !== DEVICE_SIZE.MOBILE && thumbs.length > 4);

        this.corrouselConfig$.next({
          ...carouselConfig,
          ...{ showIndicators, showNavigators: showIndicators },
        });
      }),
    );
  }

  setContent(content: MainContent) {
    this.simpleGalleryService.setContent(content);
  }

  createThumbs(product: Product): Observable<any>[] {
    const thumbs$: Observable<MainContent>[] = [];

    const hasImages =
      product.images &&
      product.images['GALLERY'] &&
      Array.isArray(product.images['GALLERY']) &&
      product.images['GALLERY'].length > 1;

    const hasVideos = product.videoUrls?.length;
    const hasImage360 = product.image360 && [product.image360]?.length;

    if (hasImages) {
      thumbs$.push(
        ...(<any[]>product.images!['GALLERY']).map(medias => of({ content: medias, type: GALLERY_MEDIA_TYPE.IMAGES })),
      );
    }

    if (hasVideos) {
      thumbs$.push(...(<any[]>product.videoUrls).map(video => of({ content: video, type: GALLERY_MEDIA_TYPE.VIDEO })));
    }

    if (hasImage360) {
      thumbs$.push(
        ...(<any[]>[product.image360]).map(image360 => of({ content: image360, type: GALLERY_MEDIA_TYPE.IMAGE_360 })),
      );
    }

    return thumbs$;
  }

  getThumbVideo(videoURL: string) {
    return this.simpleGalleryService.getThumbVideo(videoURL, YOUTUBE_THUMBNAIL.MEDIUM);
  }

  checkIsActive({ type, content }: MainContent) {
    if (type === GALLERY_MEDIA_TYPE.VIDEO) {
      return this.mainContent?.content.url === content.url;
    }

    if (type === GALLERY_MEDIA_TYPE.IMAGES) {
      return (
        this.mainContent?.content.superZoom?.url === content?.superZoom?.url &&
        this.mainContent?.content.zoom?.url === content?.zoom?.url
      );
    }

    if (type === GALLERY_MEDIA_TYPE.IMAGE_360) {
      return this.mainContent?.content?.normal === content?.normal;
    }

    return false;
  }
}
